import {
  DISC_CHANGE_CONVERSION,
  DISC_LEAST_CONVERSION,
  DISC_MOST_CONVERSION,
  DISC_TRAITS,
} from "./discMappingTables";

/**
 * Firebase category to DISC trait mapping:
 * A -> D, B -> I, C -> S, D -> C.
 */
const CAT_TO_DISC = { A: "D", B: "I", C: "S", D: "C" };
const DISC_TO_CAT = { D: "A", I: "B", S: "C", C: "D" };

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

/**
 * Clamps rawValue to the valid key range of one dimension table, then returns
 * the chart value. This preserves lookup-table behavior while protecting the
 * chart from missing or out-of-range raw scores.
 */
const lookupDimensionValue = (conversion, trait, rawValue) => {
  const dimensionTable = conversion?.[trait] || {};
  const numericKeys = Object.keys(dimensionTable).map(Number);

  if (!numericKeys.length) {
    return 0;
  }

  const minKey = Math.min(...numericKeys);
  const maxKey = Math.max(...numericKeys);
  const clampedRaw = Math.max(
    minKey,
    Math.min(maxKey, Math.round(toNumber(rawValue)))
  );

  return dimensionTable[clampedRaw] ?? dimensionTable[String(clampedRaw)] ?? 0;
};

export const buildDiscNormGroups = (conversion) =>
  DISC_TRAITS.reduce((groups, trait) => {
    const groupedByChartValue = Object.entries(conversion?.[trait] || {}).reduce(
      (traitGroups, [rawValue, chartValue]) => {
        const normalizedChartValue = toNumber(chartValue);

        if (!traitGroups[normalizedChartValue]) {
          traitGroups[normalizedChartValue] = [];
        }

        traitGroups[normalizedChartValue].push(toNumber(rawValue));
        return traitGroups;
      },
      {}
    );

    groups[trait] = Object.entries(groupedByChartValue)
      .map(([chartValue, rawValues]) => ({
        chartValue: toNumber(chartValue),
        rawValues: rawValues.sort((a, b) => b - a),
      }))
      .sort((a, b) => b.chartValue - a.chartValue);

    return groups;
  }, {});

export const getDiscRawScores = (categoryCounts, finalResults) => {
  const yes = categoryCounts?.yes || {};
  const no = categoryCounts?.no || {};

  return DISC_TRAITS.reduce(
    (scores, trait) => {
      const category = DISC_TO_CAT[trait];

      scores.most[trait] = toNumber(yes[category]);
      scores.least[trait] = toNumber(no[category]);
      scores.change[trait] = toNumber(finalResults?.[category]);

      return scores;
    },
    { most: {}, least: {}, change: {} }
  );
};

/**
 * Calculates the three DISC plotting graphs from stored Firebase result data.
 *
 * @param {Object} categoryCounts - { yes: {A,B,C,D,E}, no: {A,B,C,D,E} }
 * @param {Object} finalResults   - { A, B, C, D, E } (yes - no per category)
 *
 * @returns {{ graph1, graph2, graph3 }}
 * Each graph is { D, I, S, C } with plotted values in the -8 to +8 range.
 *
 * graph1 - MOST   (Mask / Public Self)
 * graph2 - LEAST  (Core / Private Self)
 * graph3 - CHANGE (Mirror / Perceived Self)
 */
export const calculateDiscGraphs = (categoryCounts, finalResults) => {
  const rawScores = getDiscRawScores(categoryCounts, finalResults);

  return DISC_TRAITS.reduce(
    (graphs, trait) => {
      graphs.graph1[trait] = lookupDimensionValue(
        DISC_MOST_CONVERSION,
        trait,
        rawScores.most[trait]
      );
      graphs.graph2[trait] = lookupDimensionValue(
        DISC_LEAST_CONVERSION,
        trait,
        rawScores.least[trait]
      );
      graphs.graph3[trait] = lookupDimensionValue(
        DISC_CHANGE_CONVERSION,
        trait,
        rawScores.change[trait]
      );

      return graphs;
    },
    { graph1: {}, graph2: {}, graph3: {} }
  );
};

export const buildDiscGraphDebugRows = (
  categoryCounts,
  finalResults,
  graphs = calculateDiscGraphs(categoryCounts, finalResults)
) => {
  const rawScores = getDiscRawScores(categoryCounts, finalResults);

  return DISC_TRAITS.map((trait) => ({
    trait,
    mostRaw: rawScores.most[trait],
    mostChart: graphs.graph1[trait],
    leastRaw: rawScores.least[trait],
    leastChart: graphs.graph2[trait],
    changeRaw: rawScores.change[trait],
    changeChart: graphs.graph3[trait],
  }));
};

export { CAT_TO_DISC };
